import React, { useLayoutEffect, useRef, memo, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Chart from '@toast-ui/chart';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { getSocialMediaPlatform, getPostMediaType } from '../../../../helperFunctions/getSurveysWithExtractedFields';
import { convertRange, truncateLegendText } from '../../../../helperFunctions';
import { fetchSpecificSurveyData } from '../../../../redux/actions/Data';
import {
  SocialIssuesSubjects,
  generateConsumerConsistencyData,
  generateProductBenefitsData,
  generateIssuesSupportData,
  generateSocialPositioningData,
  getPostCategory,
} from './helpers';
import DashDetailsWrapper from '../../../../myComponents/dashDetailsWrapper';
import TotalImpactDetails from '../../../../myComponents/totalImpactDetails';
import ConsumerConsistencyCard from '../../../../myComponents/chart/ConsumerConsistencyCard/ConsumerConsistencyCard';
import RangeChart from '../../../../myComponents/chart/RangeChart';
import PublishContentCard from '../../../../myComponents/chart/PublishContentCard/PublishContentCard';
import Watermark from '../../../../myComponents/watermark';
import CubeLoader from '../../../../myComponents/loaders/cubeLoader';
import MarkdownHTMLEmbed from '../../../../myComponents/markdownHTMLEmbed';
import {
  AISocialMediaStrategyRegenerator,
  generateMoodBoard,
  getMoodBoard,
  regenerateMoodBoard,
  getCampaignVideo,
  generateCampaignVideo,
  regenerateCampaignVideo,
} from '../../../../API/agent';
import {
  getCausesMatchImpactData,
  getCausesMatchImpactOptions,
  getIssuesSupportMatchImpactData,
  getIssuesSupportMatchImpactOptions,
  getProductPositioningImpactData,
  getProductPositioningImpactOptions,
  getSocialPositioningImpactData,
  getSocialPositioningImpactOptions,
  getTotalImpactData,
  getTotalImpactOptions,
} from './gaugeCharts';
import useViewportWidth from '../../../../myHooks/useViewportWidth';
import SegmentedToggle from '../../../../myComponents/SegmentedToggle';

const useStyles = makeStyles(theme => ({
  textStyle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    textAlign: 'center',
    marginTop: '10px',
  },
  productBenefitsWrapper: {
    '@media (max-width: 768px)': {
      width: '100%',
    },
  },
  rangeChartContainer: {
    marginTop: '50px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    width: '100%',

    '@media (max-width: 1260px)': {
      justifyContent: 'center',
    },
  },
  rangeChartWrapper: {
    maxWidth: '500px',
    margin: '0px 60px 100px 0px',
    width: '100%',
    '@media (max-width: 1240px)': {
      maxWidth: '600px',
    },
    '@media (max-width: 768px)': {
      maxWidth: '100%',
      width: '100%',
      margin: '0px 0px 60px 0px',
      padding: 0,
    },
  },
  consumerConsistencyContent: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: '40px',
    marginBottom: '30px',
    flexWrap: 'wrap',
    width: '100%',
    gap: '48px',

    '@media (max-width: 768px)': {
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
    },
  },
  positioningHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#FFF',
    borderBottom: '1px solid rgba(217,217,217, 0.5)',
    width: '100%',
    paddingBottom: '20px',
    fontWeight: 400,
    gap: '0px',
    '@media (max-width: 1366px)': {
      display: 'none',
    },
  },
  positioningHeaderTextElements: {
    width: '25%',
    fontSize: '22px',
    marginLeft: '-10px',
  },
  socialPositioningContentContainer: {
    width: '100%',
    '@media (max-width: 1366px)': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  socialPositioningContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#FFF',
    paddingBottom: '20px',
  },
  socialPositioningChart: {
    width: '34%',
    maxWidth: '500px',
    '@media (max-width: 1366px)': {
      width: '100%',
    },
  },

  consistency1Text: {
    width: '25%',
    '@media (max-width: 1366px)': {
      display: 'none',
    },
  },

  consistency2Text: {
    width: '25%',
    '@media (max-width: 1366px)': {
      display: 'none',
    },
  },

  chartBodyText: {
    maxWidth: '500px',
    '@media (max-width: 1366px)': {
      width: '100% !important',
    },
  },

  consistencyTextSm: {
    display: 'none',

    fontSize: '17px',
    marginTop: '32px',

    '@media (max-width: 1366px)': {
      display: 'block',
    },
  },

  consistencyName: {
    color: '#fff',
    display: 'block',
  },

  positioiningLegends: {
    width: '100%',
    display: 'flex',
    color: '#fff',
    padding: '24px 0px',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '20px',

    '@media (max-width: 1366px)': {
      marginTop: '20px',
      justifyContent: 'center',
    },

    '@media (max-width: 700px)': {
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      marginLeft: '20px',
    },
  },
  positioningLegend: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '8px',

    '& div': {
      width: '16px',
      minWidth: '16px',
      height: '16px',
      minHeight: '16px',
      borderRadius: '20px',
      boxShadow: '0px 4px 4px rgba(0,0,0,0.2)',
    },
  },
  productBenefitsLegend: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '8px',
    color: 'black',

    '& div': {
      width: '16px',
      height: '16px',
      borderRadius: '20px',
      boxShadow: '0px 4px 4px rgba(0,0,0,0.2)',
    },
  },
  subtitle: {
    width: '60%',
    color: '#A5A5A5',
    fontWeight: '17px',
    fontWeight: 300,
    lineHeight: '24px',
  },
  productBenefitsContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  consistencyBenefitsText: {
    color: '#858585',
    fontWeight: '300',
    display: 'block',
    marginTop: '8px',
    marginBottom: '8px',
  },
  contentImpact: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '32px',

    '@media (max-width: 1200px)': {
      flexWrap: 'wrap',
    },

    '@media (max-width: 768px)': {
      gap: '72px',
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
  },
  contentImpactResponsive: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-around',
    gap: '32px',
    flexWrap: 'wrap',

    '@media (max-width: 1375px)': {
      flexWrap: 'wrap',
      justifyContent: 'center',
    },

    '@media (max-width: 768px)': {
      gap: '72px',
      justifyContent: 'center',

      '& canvas': {
        marginLeft: '-40px',
      },
    },
  },
  contentImpactParagraph: {
    maxWidth: '430px',
  },
  contentImpactItem: {
    width: '400px',
    marginBottom: '30px',
    '& h3': {
      fontSize: '18px',
      fontWeight: 600,
    },
    '& h5': {
      marginTop: '16px',
      fontSize: '20px',
      width: '90%',
      fontWeight: 300,
      marginBottom: '8px',
      color: '#515151',
    },
    '& p': {
      fontSize: '14px',
      color: '#515151',
    },
    '@media (max-width: 420px)': {
      marginLeft: '30px',
    },
  },
  gaugeGraphs: {},
  heroSection: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '40px',

    '@media (max-width:1250px)': {
      flexDirection: 'column',
    },
  },
  infoSection: {
    width: 'calc(35% - 20px)',

    '@media (max-width:1250px)': {
      width: '100%',
    },
  },
  infoSectionHeader: {
    marginLeft: '150px',
    marginBottom: '24px',

    '@media (max-width: 1250px)': {
      marginLeft: '0px',
    },

    '& span': {
      fontSize: '13px',
      color: '#B3B3B3',
      display: 'block',
    },
    '& h4': {
      fontWeight: '700',
      fontSize: '18px',
      textTransform: 'uppercase',
    },
  },
  infoSectionTable: {
    width: '100%',
  },
  infoSectionTableItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    borderBottom: '1px solid rgba(0,0,0,0.05)',
    padding: '4px 0px',
  },
  infoSectionTableItemPostText: {
    width: '100%',
    borderBottom: '1px solid rgba(0,0,0,0.05)',
    padding: '4px 0px',
  },
  tableKey: {
    minWidth: '150px',
  },
  statSection: {
    width: 'calc(65% - 20px)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: '48px',

    '@media (max-width:1250px)': {
      width: '100%',
    },

    '@media (max-width: 880px)': {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  totalImpact: {
    maxWidth: 'calc(60% - 24px)',

    '& h4': {
      fontSize: '20px',
      fontWeight: 700,
      marginBottom: '12px',
      marginTop: '16px',
    },
    '& p': {
      fontSize: '15px',
      color: '#515151',
      lineHeight: '24px',
      marginBottom: '16px',
    },

    '@media (max-width: 880px)': {
      alignSelf: 'center',
      width: '100%',
      maxWidth: 'unset',
    },

    '@media (max-width: 400px)': {
      '& canvas': {
        marginLeft: '-50px',
      },
    },
  },
  totalImpactGraphContainer: {
    marginLeft: '-30px',
  },
  postEvaluation: {
    width: 'calc(40% - 24px)',

    '& h4': {
      fontSize: '20px',
      fontWeight: 700,
      marginBottom: '12px',
      marginTop: '16px',
    },
    '& p': {
      fontSize: '15px',
      color: '#515151',
      lineHeight: '24px',
      marginBottom: '16px',
    },

    '@media (max-width: 880px)': {
      width: '100%',
    },
  },
  AiTextWrapper: {
    position: 'relative',
    width: '80%',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: '20px 0 60px 0',
    color: '#E8E8E8',

    '@media (max-width: 768px)': {
      width: '90%',
    },
  },
  regenerateButton: {
    margin: '20px 0',
    display: 'flex',
    justifyContent: 'flex-end',
    '& button': {
      color: 'white',
      backgroundColor: '#19989B',
      padding: '8px 20px',
      '&:hover': {
        backgroundColor: '#19878a',
      },
    },
  },
  strategyControlsRow: {
    width: '80%',
    margin: '20px auto 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    '@media (max-width: 768px)': {
      width: '90%',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
  },
  strategyVersionControl: {
    minWidth: 170,
    borderRadius: 6,
  },
  strategyVersionSelect: {
    color: '#fff',
    backgroundColor: '#19989B',
    borderRadius: 6,
    minHeight: 40,
    '& .MuiSelect-icon': {
      color: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#19989B',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#19878a',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#19878a',
    },
  },

  generateButtonCenter: {
    margin: '20px 0',
    display: 'flex',
    justifyContent: 'center',
    '& button': {
      color: 'white',
      backgroundColor: '#19989B',
      padding: '8px 20px',
      '&:hover': {
        backgroundColor: '#19878a',
      },
    },
  },
  videoRegenerateStack: {
    margin: '20px 0',
    display: 'flex',
    justifyContent: 'flex-end',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px',
    '& button': {
      color: 'white',
      backgroundColor: '#19989B',
      padding: '6px 14px',
      fontSize: '12px',
      '&:hover': {
        backgroundColor: '#19878a',
      },
    },
    '@media (max-width: 768px)': {
      alignItems: 'stretch',
      '& button': {
        width: '100%',
      },
    },
  },
  AiLoader: {
    margin: '60px 0',
  },
  iFrameWrapper: {
    width: '100%',
    marginTop: '28px',
  },

  modeSwitcher: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '14px',
  },
  moodBoardStatus: {
    marginBottom: '16px',
    color: '#B7C2CD',
  },
  revisionDialogPaper: {
    minWidth: '760px',
    '@media (max-width: 900px)': {
      minWidth: '92vw',
      margin: 12,
    },
  },
  revisionSubtitle: {
    color: '#64748B',
    marginBottom: 16,
  },
  revisionSection: {
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    padding: 14,
    marginBottom: 14,
    background: '#F8FAFC',
  },
  revisionSectionTitle: {
    fontWeight: 700,
    color: '#0F172A',
    marginBottom: 8,
  },
  revisionError: {
    color: '#B42318',
    marginTop: 8,
    marginBottom: 8,
  },
  revisionDialogCloseBtn: {
    position: 'absolute',
    right: 8,
    top: 8,
    color: '#64748B',
  },
  moodBoardGrid: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  moodRow1: {
    display: 'grid',
    gridTemplateColumns: '1fr 3fr',
    gap: '12px',
    '@media (max-width: 768px)': { gridTemplateColumns: '1fr' },
  },
  moodRow23: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    '@media (max-width: 992px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
    '@media (max-width: 768px)': { gridTemplateColumns: '1fr' },
  },
  moodCampaignTitle: {
    fontSize: '30px',
    lineHeight: 1.2,
    marginBottom: '12px',
  },
  moodPillarTitle: {
    fontSize: '22px',
    lineHeight: 1.25,
    marginBottom: '10px',
  },

  moodPillarSummary: {
    marginBottom: '10px',
  },

  moodPillarTitleWrap: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  moodPillarSummaryWrap: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  moodPillarGroupCard: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: '8px',
    padding: '12px',
  },
  moodPillarSingleImagesRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px',
    '@media (max-width: 768px)': { 
      gridTemplateColumns: '1fr', 
    }, 
  },
  moodSectionTitle: {
    fontSize: '24px',
    fontWeight: 700,
  },
  moodImgPlaceholder: {
    width: '100%',
    borderRadius: '6px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px dashed rgba(255,255,255,0.2)',
  },
  moodCard: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: '8px',
    padding: '12px',
    minHeight: '120px',
  },
  moodImg: {
    width: '100%',
    borderRadius: '6px',
    objectFit: 'cover',
    background: '#1f2d3a',
  },

  productBenefitsResponsive: {
    marginTop: '50px',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    width: '100%',

    '@media (max-width: 1260px)': {
      justifyContent: 'center',
    },
  },
}));

const REVISION_CHANGE_OPTIONS = [
  { key: 'story_message', label: 'Story / message' },
  { key: 'visual_style', label: 'Visual style' },
  { key: 'tone_emotion', label: 'Tone / emotion' },
  { key: 'pacing_structure', label: 'Pacing / structure' },
  { key: 'opening', label: 'Opening (first seconds)' },
  { key: 'ending', label: 'Ending (final message)' },
];

const STRATEGY_REGEN_CHANGE_OPTIONS = [
  { key: 'objective', label: 'Objective' },
  { key: 'target_audience', label: 'Target Audience' },
  { key: 'customer_insight', label: 'Customer Insight' },
  { key: 'messaging_guardrails', label: 'Messaging Guardrails' },
  { key: 'campaign_approach', label: 'Campaign Approach / Strategic Execution' },
  { key: 'revised_copy', label: 'Revised Copy' },
  { key: 'budget_allocation', label: 'Budget & Allocation' },
  { key: 'timeline_execution', label: 'Timeline & Execution Plan' },
];

const STRATEGY_BUDGET_CURRENCIES = [
  { label: 'USD', value: 'usd' },
  { label: 'EUR', value: 'eur' },
  { label: 'GBP', value: 'gbp' },
];

const ChartCalculations = ({ data, persona, socialPost, AIStrategyGenerator, setAIStrategyGenerator, visibleAgencies }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const location = useLocation();
  const totalMatchChartRef = useRef(null);
  const productPositioningMatchChartRef = useRef(null);
  const socialPositioningMatchChartRef = useRef(null);
  const causesMatchChartRef = useRef(null);
  const issuesSupportMatchChartRef = useRef(null);
  const isReportExport = location.pathname.includes('/report-export/');
  const issuesImpact = generateConsumerConsistencyData(
    SocialIssuesSubjects,
    data.social_post_data.IssuesImpact,
    isReportExport,
  );

  const viewportWidth = useViewportWidth();
  const isMobileViewport = viewportWidth <= 768;

  // Total impact gauge chart
  useLayoutEffect(() => {
    const totalImpactGaugeChartEl = document.querySelector('#totalImpactGaugeChart');

    if (!data) return;

    let totalMatchValueLabel = data?.social_post_data?.TotalImpact?.value;
    totalMatchValueLabel = Math.round(Number(totalMatchValueLabel) * 100);
    let totalMatchRawValue = convertRange(totalMatchValueLabel, [-100, 100], [0, 100]);

    const totalImpactData = getTotalImpactData(totalMatchRawValue);

    const totalImpactOptions = getTotalImpactOptions(totalMatchValueLabel, viewportWidth, isReportExport);

    if (!totalMatchChartRef.current) {
      totalMatchChartRef.current = Chart.gaugeChart({
        el: totalImpactGaugeChartEl,
        data: totalImpactData,
        options: totalImpactOptions,
      });
    } else {
      totalMatchChartRef.current.setData(totalImpactData);
      totalMatchChartRef.current.setOptions(totalImpactOptions);
    }
  }, [data]);

  // productPositioningGaugeChart
  useLayoutEffect(() => {
    const productPositioningGaugeChartEl = document.querySelector('#productPositioningGaugeChart');

    if (!data) return;

    let productMatchValueLabel = data?.social_post_data?.ProductImpact?.TotalProductImpact?.value;
    productMatchValueLabel = Math.round(Number(productMatchValueLabel) * 100);
    let productMatchRawValue = convertRange(productMatchValueLabel, [-100, 100], [0, 100]);

    const productPositioningImpactData = getProductPositioningImpactData(productMatchRawValue);

    const productPositioningImpactOptions = getProductPositioningImpactOptions(productMatchValueLabel, isReportExport);

    if (!productPositioningMatchChartRef.current) {
      productPositioningMatchChartRef.current = Chart.gaugeChart({
        el: productPositioningGaugeChartEl,
        data: productPositioningImpactData,
        options: productPositioningImpactOptions,
      });
    } else {
      productPositioningMatchChartRef.current.setData(productPositioningImpactData);
      productPositioningMatchChartRef.current.setOptions(productPositioningImpactOptions);
    }
  }, [data]);

  // socialPositioningImpactData
  useLayoutEffect(() => {
    const socialPositioningGaugeChartEl = document.querySelector('#socialPositioningGaugeChart');

    if (!data) return;

    let socialPositioningMatchValueLabel =
      data?.social_post_data?.SocialPositioningImpact?.TotalSocialPositioningImpact?.value;
    socialPositioningMatchValueLabel = Math.round(Number(socialPositioningMatchValueLabel) * 100);
    let ocialPositioningMatchRawValue = convertRange(socialPositioningMatchValueLabel, [-100, 100], [0, 100]);

    const socialPositioningImpactData = getSocialPositioningImpactData(ocialPositioningMatchRawValue);

    const socialPositioningImpactOptions = getSocialPositioningImpactOptions(
      socialPositioningMatchValueLabel,
      isReportExport,
    );

    if (!socialPositioningMatchChartRef.current) {
      socialPositioningMatchChartRef.current = Chart.gaugeChart({
        el: socialPositioningGaugeChartEl,
        data: socialPositioningImpactData,
        options: socialPositioningImpactOptions,
      });
    } else {
      socialPositioningMatchChartRef.current.setData(socialPositioningImpactData);
      socialPositioningMatchChartRef.current.setOptions(socialPositioningImpactOptions);
    }
  }, [data]);

  ///
  useLayoutEffect(() => {
    const causesMatchImpactGaugeChartEl = document.querySelector('#causesMatchImpactGaugeChart');

    if (!data) return;

    let issuesMatchValueLabel = data?.social_post_data?.IssuesImpact?.TotalIssuesImpact?.value;
    issuesMatchValueLabel = Math.round(Number(issuesMatchValueLabel) * 100);
    let issuesMatchRawValue = convertRange(issuesMatchValueLabel, [-100, 100], [0, 100]);

    const causesMatchImpactData = getCausesMatchImpactData(issuesMatchRawValue);

    const causesMatchImpactOptions = getCausesMatchImpactOptions(issuesMatchValueLabel, isReportExport);

    if (!causesMatchChartRef.current) {
      causesMatchChartRef.current = Chart.gaugeChart({
        el: causesMatchImpactGaugeChartEl,
        data: causesMatchImpactData,
        options: causesMatchImpactOptions,
      });
    } else {
      causesMatchChartRef.current.setData(causesMatchImpactData);
      causesMatchChartRef.current.setOptions(causesMatchImpactOptions);
    }
  }, [data]);

  useLayoutEffect(() => {
    const issuesSupportImpactGaugeChartEl = document.querySelector('#issuesSupportImpactGaugeChart');

    if (!data) return;

    let issuesSupportMatchValueLabel = data?.social_post_data?.IssuesSupportImpact?.TotalIssuesSupportImpact?.value;
    issuesSupportMatchValueLabel = Math.round(Number(issuesSupportMatchValueLabel) * 100);
    let issuesSupportMatchRawValue = convertRange(issuesSupportMatchValueLabel, [-100, 100], [0, 100]);

    const issuesSupportMatchImpactData = getIssuesSupportMatchImpactData(issuesSupportMatchRawValue);

    const issuesSupportMatchImpactOptions = getIssuesSupportMatchImpactOptions(issuesSupportMatchValueLabel, isReportExport);

    if (!issuesSupportMatchChartRef.current) {
      issuesSupportMatchChartRef.current = Chart.gaugeChart({
        el: issuesSupportImpactGaugeChartEl,
        data: issuesSupportMatchImpactData,
        options: issuesSupportMatchImpactOptions,
      });
    } else {
      issuesSupportMatchChartRef.current.setData(issuesSupportMatchImpactData);
      issuesSupportMatchChartRef.current.setOptions(issuesSupportMatchImpactOptions);
    }
  }, [data]);

  const consumerPositioningImpactData = data.personal_average.SocialValues;
  const socialPositioningImpact = data.social_post_data.SocialPositioningImpact;
  const personaPositioningImpactData = {
    IdeologyImpact: data.brand_persona_data.Brand_Ideology,
    InternalExternalImpact: data.brand_persona_data.Brand_InterExter,
    LocalGlobalImpact: data.brand_persona_data.Brand_LocalGlobal,
    SocialInovatImpact: data.brand_persona_data.Brand_SocialInovation,
  };

  const socialPositioningData = generateSocialPositioningData(
    consumerPositioningImpactData,
    socialPositioningImpact,
    personaPositioningImpactData,
    socialPost,
  );

  const consumerProductBenefits = data.personal_average.PersonalProduct;
  const socialProductBenefits = data.social_post_data.ProductImpact;
  let personaProductBenefits = {};

  for (let i = 0; i < 10; i++) {
    let count = i + 1;

    let name = 'Brand_Product_' + count;

    personaProductBenefits = {
      ...personaProductBenefits,
      [name]: persona['Brand_Product_' + count],
    };
  }

  const productBenefitsData = generateProductBenefitsData(
    consumerProductBenefits,
    socialProductBenefits,
    personaProductBenefits,
    socialPost,
  );

  const consumerIssuesSupport = data.personal_average.PersonalIssuesSupport;
  const socialIssuesSupport = data.social_post_data.IssuesSupportImpact;
  let personaIssuesSupport = {};

  for (let i = 0; i < 18; i++) {
    let count = i + 1;

    let name = 'Brand_IssuesSupport_' + count;

    personaIssuesSupport = {
      ...personaIssuesSupport,
      [name]: persona['Brand_IssuesSupport_' + count],
    };
  }

  const issuesSupportData = generateIssuesSupportData(
    consumerIssuesSupport,
    socialIssuesSupport,
    personaIssuesSupport,
    socialPost,
  );

  const socialPositionImpactData = data.social_post_data.SocialPositioningImpact.TotalSocialPositioningImpact;
  const socialCausesImpactData = data.social_post_data.IssuesImpact.TotalIssuesImpact;
  const productPositionImpactData = data.social_post_data.ProductImpact.TotalProductImpact;
  const issuesSupportImpactData = data.social_post_data.IssuesSupportImpact.TotalIssuesSupportImpact;

  const getConsistencyTextColor = text => {
    const normalized = (text || '').toLowerCase();
    let color = isReportExport ? '#19989B' : '#87edef';

    if (normalized == 'not applicable') {
      color = '#979797';
    } else if (normalized.startsWith('inconsistent')) {
      color = '#ed8fb3';
    }

    return color;
  };

  let postText = '';

  socialPost.Post_Text.forEach(text => {
    if (text.isSelected) {
      postText = text.value;
    }
  });

  const getYouTubeEmbedUrl = url => {
    if (!url) return null;

    try {
      const parsed = new URL(url);
      const host = parsed.hostname.replace('www.', '').toLowerCase();

      if (host === 'youtube.com' || host === 'm.youtube.com') {
        if (parsed.pathname.startsWith('/shorts/')) {
          const shortsId = parsed.pathname.split('/shorts/')[1]?.split('/')[0];
          if (!shortsId) return null;
          const origin = encodeURIComponent(window.location.origin);
          return `https://www.youtube.com/embed/${shortsId}?origin=${origin}&rel=0&modestbranding=1`;
        }

        const videoId = parsed.searchParams.get('v');
        if (!videoId) return null;
        const origin = encodeURIComponent(window.location.origin);
        return `https://www.youtube.com/embed/${videoId}?origin=${origin}&rel=0&modestbranding=1`;
      }

      if (host === 'youtu.be') {
        const videoId = parsed.pathname.replace('/', '').split('/')[0];
        if (!videoId) return null;
        const origin = encodeURIComponent(window.location.origin);
        return `https://www.youtube.com/embed/${videoId}?origin=${origin}&rel=0&modestbranding=1`;
      }
    } catch (error) {
      return null;
    }

    return null;
  };

  const youtubeEmbedUrl = getYouTubeEmbedUrl(socialPost.Post_Link);

  useEffect(() => {
    setYoutubeEmbedFailed(false);
  }, [youtubeEmbedUrl]);

  const getProductBenefitsSpanColor = text => {
    let color = '#19989B';

    if (text.toLowerCase() == 'not applicable') {
      color = '#979797';
    } else if (text.toLowerCase().startsWith('inconsistent')) {
      color = '#CE5378';
    }

    return color;
  };

  const [strategyVersions, setStrategyVersions] = useState([]);
  const [selectedStrategyVersion, setSelectedStrategyVersion] = useState(1);
  const [strategyRevisionDialogOpen, setStrategyRevisionDialogOpen] = useState(false);
  const [strategyRevisionForm, setStrategyRevisionForm] = useState({
    selectedChanges: [],
    objectiveUpdate: '',
    audienceUpdate: '',
    customerInsightUpdate: '',
    messagingGuardrailsUpdate: '',
    campaignApproachUpdate: '',
    revisedCopyDirection: '',
    budgetAmount: '',
    budgetCurrency: socialPost?.Post_Budget?.currency || 'usd',
    budgetDistribution: '',
    timelineUpdate: '',
    additionalNotes: '',
  });
  const selectedStrategyEntry = strategyVersions.find(version => version.versionNumber === selectedStrategyVersion) || null;
  const aiText = selectedStrategyEntry?.text || AIStrategyGenerator.text;
  const isSocialMedia = socialPost.Post_Media_Type == 1;
  const [activeMode, setActiveMode] = useState('strategy');
  const [youtubeEmbedFailed, setYoutubeEmbedFailed] = useState(false);
  const [moodBoardState, setMoodBoardState] = useState({ loading: false, status: 'idle', data: null, error: '' });
  const [videoState, setVideoState] = useState({
    seedance: { status: 'idle', data: null, history: [], error: '' },
    quota: null,
    eligible: false,
    seedanceScriptReady: false,
    isGenerating: false,
    videoMode: null,
  });
  const [revisionState, setRevisionState] = useState({
    open: false,
    regenerationMode: null,
    baseVideoId: '',
    baseVersionNumber: null,
    baseVideoLabel: '',
    step: 'questions',
    selectedChanges: [],
    storyMessageChange: '',
    visualStyle: '',
    visualStyleNotes: '',
    toneEmotion: '',
    toneNotes: '',
    pacingStructure: '',
    sceneDensity: '',
    structureNotes: '',
    openingType: '',
    openingNotes: '',
    endingGoals: [],
    endingNotes: '',
    submitError: '',
    isSubmitting: false,
    isRevisingScript: false,
    isGeneratingVideo: false,
    scriptRevisionError: '',
    videoGenerationError: '',
  });
  const providerToLabel = () => 'Direct';
  const toMillis = value => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    if (typeof value?.toMillis === 'function') return value.toMillis();
    if (typeof value?._seconds === 'number') return value._seconds * 1000;
    return 0;
  };
  const getVideoSortKey = video => (
    toMillis(video?.createdAt)
    || toMillis(video?.archivedAt)
    || toMillis(video?.updatedAt)
    || 0
  );
  const allVideos = (() => {
    const candidates = [
      { ...videoState.seedance.data, provider: videoState.seedance.data?.provider || 'seedance' },
      ...(videoState.seedance.history || []).map(video => ({ ...video, provider: video?.provider || 'seedance' })),
    ].filter(video => video?.downloadURL);

    const deduped = [];
    const seen = new Set();
    candidates.forEach(video => {
      const key = video.videoId || video.storagePath || video.atlasJobId || video.downloadURL;
      if (!key || seen.has(key)) return;
      seen.add(key);
      deduped.push(video);
    });
    const sortedAsc = [...deduped].sort((a, b) => {
      const createdDiff = getVideoSortKey(a) - getVideoSortKey(b);
      if (createdDiff !== 0) return createdDiff;
      const aId = String(a.videoId || a.atlasJobId || a.storagePath || '');
      const bId = String(b.videoId || b.atlasJobId || b.storagePath || '');
      return aId.localeCompare(bId);
    }).map((video, index) => ({
      ...video,
      normalizedVersionNumber: Number(video?.versionNumber) || (index + 1),
    }));

    return sortedAsc.sort((a, b) => {
      const createdDiff = getVideoSortKey(b) - getVideoSortKey(a);
      if (createdDiff !== 0) return createdDiff;
      const versionDiff = Number(b.normalizedVersionNumber || 0) - Number(a.normalizedVersionNumber || 0);
      if (versionDiff !== 0) return versionDiff;
      const aId = String(a.videoId || a.atlasJobId || a.storagePath || '');
      const bId = String(b.videoId || b.atlasJobId || b.storagePath || '');
      return bId.localeCompare(aId);
    });
  })();
  const hasAnyVideo = allVideos.length > 0;
  const hasDirectVideo = allVideos.some(video => video.provider === 'seedance');
  const revisionCandidates = allVideos.filter(video => video.provider === 'seedance');
  const configuredPillarIndices = [1, 2, 3].filter(idx => Boolean(socialPost[`Post_Keyword_${idx}`]));
  const isVideoEligible =
    Number(socialPost.Post_Media_Type) === 2 ||
    (Number(socialPost.Post_Media_Type) === 1 && [2, 4, 6].includes(Number(socialPost.Post_Social_Media)));
  const openContactSalesPage = () => {
    const contactSalesUrl = videoState?.quota?.contactSalesUrl || (window.location.hostname.includes('staging')
      ? 'https://staging-www.brandsocialvalue.com/contact/'
      : 'https://www.brandsocialvalue.com/contact/');
    window.open(contactSalesUrl, '_blank', 'noopener,noreferrer');
  };

  const runningVideoStatuses = ['queued', 'running', 'generating', 'processing', 'pending_recovery', 'timeout_retryable'];
  const isVideoStatusRunning = status => runningVideoStatuses.includes(status);

  const buildProviderVideoState = (providerResult, previousProviderState) => {
    const status = providerResult?.video?.status || previousProviderState?.status || 'idle';
    const videoData = providerResult?.video || null;
    const providerError = typeof providerResult?.video?.error === 'string' ? providerResult.video.error.trim() : '';
    const providerWarning = typeof providerResult?.video?.finalizationWarning === 'string' ? providerResult.video.finalizationWarning.trim() : '';
    const error = [providerError, providerWarning].filter(Boolean).join(' ');

    return {
      status,
      data: videoData,
      history: Array.isArray(providerResult?.videoHistory) ? providerResult.videoHistory : (previousProviderState?.history || []),
      error,
    };
  };

  const [moodTitleRowHeight, setMoodTitleRowHeight] = useState(0);
  const [moodSummaryRowHeight, setMoodSummaryRowHeight] = useState(0);
  const pillarTitleRefs = useRef({});
  const pillarSummaryRefs = useRef({});
  const videoRequestPendingRef = useRef(false);
  const videoGenerationStoppedRef = useRef(false);

  useEffect(() => {
    if (isReportExport || activeMode !== 'moodboard') return;
    let mounted = true;

    const load = async () => {
      setMoodBoardState(prev => ({ ...prev, loading: true, error: '' }));
      try {
        const result = await getMoodBoard(socialPost.id, persona.id);
        if (!mounted) return;
        const status = result?.status || 'idle';
        const stillRunning = ['queued', 'running'].includes(status);
        setMoodBoardState({ loading: stillRunning, status, data: result, error: result?.error_message || '' });
      } catch (err) {
        if (!mounted) return;
        setMoodBoardState({ loading: false, status: 'failed', data: null, error: err.message || 'Failed to load mood board' });
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [activeMode, isReportExport, persona.id, socialPost.id]);

  useEffect(() => {
    if (activeMode !== 'moodboard') return undefined;
    if (!['queued', 'running'].includes(moodBoardState.status)) return undefined;

    const interval = setInterval(async () => {
      try {
        const result = await getMoodBoard(socialPost.id, persona.id);
        const status = result?.status || 'idle';
        const stillRunning = ['queued', 'running'].includes(status);
        setMoodBoardState({ loading: stillRunning, status, data: result, error: result?.error_message || '' });
      } catch (err) {
        setMoodBoardState(prev => ({ ...prev, loading: false, status: 'failed', error: err.message || 'Polling failed' }));
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [activeMode, moodBoardState.status, persona.id, socialPost.id]);

  useEffect(() => {
    if (isReportExport || activeMode !== 'video') return;
    if (!isVideoEligible) {
      setVideoState({
        seedance: { status: 'idle', data: null, history: [], error: '' },
        quota: null,
        eligible: false,
        seedanceScriptReady: false,
        isGenerating: false,
        videoMode: null,
      });
      return;
    }

    let mounted = true;

    const load = async () => {
      try {
        const seedanceResult = await getCampaignVideo(socialPost.id, persona.id);
        if (!mounted) return;

        const seedanceStatus = seedanceResult?.video?.status || 'idle';
        const generating = !videoGenerationStoppedRef.current && (
          videoRequestPendingRef.current || isVideoStatusRunning(seedanceStatus)
        );

        setVideoState(prev => ({
          ...prev,
          seedance: buildProviderVideoState(seedanceResult, prev.seedance),
          quota: seedanceResult?.quota || null,
          eligible: Boolean(seedanceResult?.eligible),
          seedanceScriptReady: Boolean(seedanceResult?.videoScriptReady),
          isGenerating: generating,
          videoMode: prev.videoMode,
        }));
      } catch (err) {
        if (!mounted) return;
        setVideoState(prev => ({
          ...prev,
          isGenerating: false,
          seedance: { ...prev.seedance, error: err.message || 'Failed to load video state' },
        }));
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [activeMode, isReportExport, isVideoEligible, persona.id, socialPost.id]);

  useEffect(() => {
    if (activeMode !== 'video' || !isVideoEligible) return undefined;
    if (!videoState.isGenerating) return undefined;

    const interval = setInterval(async () => {
      try {
        const seedanceResult = await getCampaignVideo(socialPost.id, persona.id);

        const seedanceStatus = seedanceResult?.video?.status || 'idle';
        const generating = !videoGenerationStoppedRef.current && (
          videoRequestPendingRef.current || isVideoStatusRunning(seedanceStatus)
        );

        setVideoState(prev => ({
          ...prev,
          seedance: buildProviderVideoState(seedanceResult, prev.seedance),
          quota: seedanceResult?.quota || prev.quota,
          eligible: Boolean(seedanceResult?.eligible),
          seedanceScriptReady: Boolean(seedanceResult?.videoScriptReady),
          isGenerating: generating,
        }));
      } catch (err) {
        setVideoState(prev => ({ ...prev, isGenerating: false, seedance: { ...prev.seedance, error: err.message || 'Failed to refresh video state' } }));
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [activeMode, isVideoEligible, persona.id, socialPost.id, videoState.isGenerating]);

  const stopVideoGeneration = () => {
    videoRequestPendingRef.current = false;
    videoGenerationStoppedRef.current = true;
    setVideoState(prev => ({
      ...prev,
      isGenerating: false,
      videoMode: null,
      seedance: { ...prev.seedance, error: '' },
    }));
    setRevisionState(prev => ({ ...prev, isSubmitting: false, isGeneratingVideo: false }));
  };

  const triggerVideoGeneration = async mode => {
    if (videoState.isGenerating) return;
    videoGenerationStoppedRef.current = false;
    if (videoState.quota?.monthlyLimit === 0 || videoState.quota?.reached) {
      setVideoState(prev => ({
        ...prev,
        [mode]: {
          ...prev[mode],
          status: 'failed',
          error: 'Monthly video limit reached. Please contact sales to increase your video generation quota.',
        },
      }));
      return;
    }

    videoRequestPendingRef.current = true;

    setVideoState(prev => ({
      ...prev,
      isGenerating: true,
      videoMode: mode,
      seedance: { ...prev.seedance, status: 'generating', error: '' },
    }));

    try {
      await generateCampaignVideo(socialPost.id, persona.id);

      const seedanceFresh = await getCampaignVideo(socialPost.id, persona.id);

      const seedanceStatus = seedanceFresh?.video?.status || 'idle';
      const generating = !videoGenerationStoppedRef.current && isVideoStatusRunning(seedanceStatus);
      videoRequestPendingRef.current = false;

      setVideoState(prev => ({
        ...prev,
        seedance: buildProviderVideoState(seedanceFresh, prev.seedance),
        quota: seedanceFresh?.quota || prev.quota,
        eligible: Boolean(seedanceFresh?.eligible),
        seedanceScriptReady: Boolean(seedanceFresh?.videoScriptReady),
        isGenerating: generating,
        videoMode: mode,
      }));
    } catch (err) {
      videoRequestPendingRef.current = false;
      const backendMessage = err?.response?.data?.result?.message
        || err?.response?.data?.result
        || err?.message
        || 'Video generation failed';
      setVideoState(prev => ({
        ...prev,
        isGenerating: false,
        [mode]: { ...prev[mode], status: 'failed', error: backendMessage },
      }));
    }
  };
  const openRevisionFlow = mode => {
    const regenerationMode = 'direct';
    const modeVideos = allVideos.filter(video => video.provider === 'seedance');
    if (!modeVideos.length) {
      triggerVideoGeneration(mode);
      return;
    }
    const baseVideo = modeVideos.length === 1 ? modeVideos[0] : null;
    const draftKey = `videoRevisionDraft:${socialPost.id}:${baseVideo?.videoId || baseVideo?.atlasJobId || 'pending'}:${regenerationMode}`;
    let draft = {};
    try {
      draft = JSON.parse(localStorage.getItem(draftKey) || '{}');
    } catch (err) {
      draft = {};
    }
    setRevisionState(prev => ({
      ...prev,
      open: true,
      regenerationMode,
      baseVideoId: baseVideo?.videoId || baseVideo?.atlasJobId || '',
      baseVersionNumber: Number(baseVideo?.normalizedVersionNumber || null),
      baseVideoLabel: baseVideo ? `Version ${baseVideo.normalizedVersionNumber} — ${providerToLabel(baseVideo.provider)}` : '',
      step: baseVideo ? 'questions' : 'select_base',
      selectedChanges: draft.selectedChanges || prev.selectedChanges,
      storyMessageChange: draft.storyMessageChange || prev.storyMessageChange,
      visualStyle: draft.visualStyle || prev.visualStyle,
      visualStyleNotes: draft.visualStyleNotes || prev.visualStyleNotes,
      toneEmotion: draft.toneEmotion || prev.toneEmotion,
      toneNotes: draft.toneNotes || prev.toneNotes,
      pacingStructure: draft.pacingStructure || prev.pacingStructure,
      sceneDensity: draft.sceneDensity || prev.sceneDensity,
      structureNotes: draft.structureNotes || prev.structureNotes,
      openingType: draft.openingType || prev.openingType,
      openingNotes: draft.openingNotes || prev.openingNotes,
      endingGoals: draft.endingGoals || prev.endingGoals,
      endingNotes: draft.endingNotes || prev.endingNotes,
      submitError: '',
      scriptRevisionError: '',
      videoGenerationError: '',
    }));
  };

  const closeRevisionFlow = () => {
    if (revisionState.isSubmitting) return;
    if (revisionState.baseVideoId && revisionState.regenerationMode) {
      const draftKey = `videoRevisionDraft:${socialPost.id}:${revisionState.baseVideoId}:${revisionState.regenerationMode}`;
      localStorage.setItem(draftKey, JSON.stringify({
        selectedChanges: revisionState.selectedChanges,
        storyMessageChange: revisionState.storyMessageChange,
        visualStyle: revisionState.visualStyle,
        visualStyleNotes: revisionState.visualStyleNotes,
        toneEmotion: revisionState.toneEmotion,
        toneNotes: revisionState.toneNotes,
        pacingStructure: revisionState.pacingStructure,
        sceneDensity: revisionState.sceneDensity,
        structureNotes: revisionState.structureNotes,
        openingType: revisionState.openingType,
        openingNotes: revisionState.openingNotes,
        endingGoals: revisionState.endingGoals,
        endingNotes: revisionState.endingNotes,
      }));
    }
    setRevisionState(prev => ({ ...prev, open: false, submitError: '', scriptRevisionError: '', videoGenerationError: '' }));
  };

  const toggleRevisionChange = key => {
    setRevisionState(prev => {
      const exists = prev.selectedChanges.includes(key);
      return {
        ...prev,
        selectedChanges: exists ? prev.selectedChanges.filter(item => item !== key) : [...prev.selectedChanges, key],
      };
    });
  };

  const submitRevision = async () => {
    if (!revisionState.baseVideoId) {
      setRevisionState(prev => ({ ...prev, submitError: 'Please select which version to revise.' }));
      return;
    }
    if (!revisionState.selectedChanges.length) {
      setRevisionState(prev => ({ ...prev, submitError: 'Please choose at least one area to revise.' }));
      return;
    }
    if (revisionState.selectedChanges.includes('story_message') && !revisionState.storyMessageChange.trim()) {
      setRevisionState(prev => ({ ...prev, submitError: 'Please describe how the story/message should change.' }));
      return;
    }

    const providerMode = 'seedance';
    const revisionInputs = {
      selectedChanges: revisionState.selectedChanges,
      storyMessageChange: revisionState.storyMessageChange,
      visualStyle: revisionState.visualStyle,
      visualStyleNotes: revisionState.visualStyleNotes,
      toneEmotion: revisionState.toneEmotion,
      toneNotes: revisionState.toneNotes,
      pacingStructure: revisionState.pacingStructure,
      sceneDensity: revisionState.sceneDensity,
      structureNotes: revisionState.structureNotes,
      openingType: revisionState.openingType,
      openingNotes: revisionState.openingNotes,
      endingGoals: revisionState.endingGoals,
      endingNotes: revisionState.endingNotes,
    };

    videoRequestPendingRef.current = true;
    videoGenerationStoppedRef.current = false;
    setRevisionState(prev => ({
      ...prev,
      open: false,
      submitError: '',
      isSubmitting: true,
      isRevisingScript: false,
      isGeneratingVideo: false,
      scriptRevisionError: '',
      videoGenerationError: '',
    }));

    setVideoState(prev => ({
      ...prev,
      isGenerating: true,
      videoMode: providerMode,
      [providerMode]: { ...prev[providerMode], status: 'generating', error: '' },
    }));

    try {
      await regenerateCampaignVideo({
        socialPostId: socialPost.id,
        brandPersonaId: persona.id,
        baseVideoId: revisionState.baseVideoId,
        baseVersionNumber: revisionState.baseVersionNumber,
        regenerationMode: revisionState.regenerationMode,
        revisionInputs,
      });

      const seedanceFresh = await getCampaignVideo(socialPost.id, persona.id);
      const seedanceStatus = seedanceFresh?.video?.status || 'idle';
      const generating = !videoGenerationStoppedRef.current && isVideoStatusRunning(seedanceStatus);
      videoRequestPendingRef.current = false;

      setVideoState(prev => ({
        ...prev,
        seedance: buildProviderVideoState(seedanceFresh, prev.seedance),
        quota: seedanceFresh?.quota || prev.quota,
        eligible: Boolean(seedanceFresh?.eligible),
        seedanceScriptReady: Boolean(seedanceFresh?.videoScriptReady),
        isGenerating: generating,
        videoMode: providerMode,
      }));
      setRevisionState(prev => ({ ...prev, isSubmitting: false, isGeneratingVideo: false }));
    } catch (err) {
      videoRequestPendingRef.current = false;
      const backendMessage = err?.response?.data?.result?.message
        || err?.response?.data?.result
        || err?.message
        || 'Video regeneration failed';
      setRevisionState(prev => ({
        ...prev,
        isSubmitting: false,
        isGeneratingVideo: false,
        scriptRevisionError: '',
        videoGenerationError: '',
      }));
      setVideoState(prev => ({
        ...prev,
        isGenerating: false,
        [providerMode]: { ...prev[providerMode], status: 'failed', error: backendMessage },
      }));
    }
  };
  useLayoutEffect(() => {
    if (!moodBoardState.data) {
      setMoodTitleRowHeight(0);
      setMoodSummaryRowHeight(0);
      return;
    }

    const titleHeights = (configuredPillarIndices.length ? configuredPillarIndices : [1])
      .map(idx => pillarTitleRefs.current[idx]?.offsetHeight || 0)
      .filter(Boolean);
    const summaryHeights = (configuredPillarIndices.length ? configuredPillarIndices : [1])
      .map(idx => pillarSummaryRefs.current[idx]?.offsetHeight || 0)
      .filter(Boolean);

    setMoodTitleRowHeight(titleHeights.length ? Math.max(...titleHeights) : 0);
    setMoodSummaryRowHeight(summaryHeights.length ? Math.max(...summaryHeights) : 0);
  }, [configuredPillarIndices, moodBoardState.data?.summaries]);


  const canShowMoodBoardGrid = moodBoardState.data && ['running', 'queued', 'completed'].includes(moodBoardState.status);
  const canShowMoodBoardGenerate = !moodBoardState.loading && (moodBoardState.status === 'idle' || moodBoardState.status === 'failed');

  const getMoodTile = key => moodBoardState.data?.tiles?.[key]?.url;

  const buildMoodBoardSkeleton = () => {
    const pillarCandidates = [socialPost.Post_Keyword_1, socialPost.Post_Keyword_2, socialPost.Post_Keyword_3].filter(Boolean);
    const summaries = { campaign_summary_100: '' };
    (configuredPillarIndices.length ? configuredPillarIndices : [1]).forEach(idx => {
      summaries[`pillar${idx}_title`] = pillarCandidates[idx - 1] || '';
      summaries[`pillar${idx}_summary_50`] = '';
    });
    const tiles = {
      campaign_main: { status: 'pending' },
      palette_tile: { status: 'pending' },
      texture_tile: { status: 'pending' },
      typography_final: { status: 'pending' },
      pillar1_main: { status: 'pending' },
      pillar1_supporting: { status: 'pending' },
      pillar2_main: { status: 'pending' },
      pillar2_supporting: { status: 'pending' },
      pillar3_main: { status: 'pending' },
      pillar3_supporting: { status: 'pending' },
    };
    return { status: 'running', summaries, tiles };
  };

  const triggerMoodBoard = async regenerate => {
    setMoodBoardState(prev => ({ ...prev, loading: true, status: 'running', data: prev.data || buildMoodBoardSkeleton(), error: '' }));
    try {
      const response = regenerate
        ? await regenerateMoodBoard(socialPost.id, persona.id)
        : await generateMoodBoard(socialPost.id, persona.id);
      const status = response.result?.status || 'completed';
      setMoodBoardState({ loading: ['queued', 'running'].includes(status), status, data: response.result, error: response.result?.error_message || '' });
      dispatch(fetchSpecificSurveyData('social', visibleAgencies));
    } catch (err) {
      setMoodBoardState({ loading: false, status: 'failed', data: null, error: err.message || 'Mood board generation failed' });
    }
  };

  useEffect(() => {
    const existingVersions = Array.isArray(socialPost?.marketingStrategyVersions?.[persona.id])
      ? socialPost.marketingStrategyVersions[persona.id]
      : [];
    const fallbackText = AIStrategyGenerator?.text || socialPost?.marketingStrategies?.[persona.id] || '';

    const normalizedFromVersions = existingVersions.length
      ? existingVersions
        .map((item, index) => ({
          versionNumber: Number(item?.versionNumber) || index + 1,
          text: item?.text || item?.value || item?.strategy || item?.content || '',
          status: 'ready',
        }))
        .filter(item => item.text)
      : [];

    const normalized = normalizedFromVersions.length
      ? normalizedFromVersions
      : (fallbackText ? [{ versionNumber: 1, text: fallbackText, status: 'ready' }] : []);

    const hasLocalGenerating = strategyVersions.some(item => item.status === 'generating');
    const localMaxVersion = strategyVersions.length ? Math.max(...strategyVersions.map(item => Number(item.versionNumber) || 0)) : 0;
    const normalizedMaxVersion = normalized.length ? Math.max(...normalized.map(item => Number(item.versionNumber) || 0)) : 0;
    const shouldAdoptNormalized = !hasLocalGenerating && normalized.length && normalizedMaxVersion >= localMaxVersion;

    setStrategyVersions(prev => {
      const hasGenerating = prev.some(item => item.status === 'generating');
      if (hasGenerating) return prev;
      const prevMaxVersion = prev.length ? Math.max(...prev.map(item => Number(item.versionNumber) || 0)) : 0;
      if (normalized.length && normalizedMaxVersion >= prevMaxVersion) return normalized;
      if (prev.length && prevMaxVersion > normalizedMaxVersion) return prev;
      return prev.length ? prev : normalized;
    });
    if (shouldAdoptNormalized) {
      setSelectedStrategyVersion(normalized[normalized.length - 1].versionNumber);
    }
  }, [socialPost?.id, persona?.id, socialPost?.marketingStrategyVersions, socialPost?.marketingStrategies, AIStrategyGenerator?.text]);

  const buildStrategyData = () => {

    const {
      TotalImpact,
      ProductImpact,
      SocialPositioningImpact,
      IssuesImpact,
      IssuesSupportImpact,
    } = data?.social_post_data;

    const contentType = getPostMediaType(socialPost);
    const platform = isSocialMedia ? getSocialMediaPlatform(socialPost) : null;

    return {
      postTitle: socialPost.Post_Title,
      brandName: persona.Name,
      contentType: contentType,
      platform: platform,
      postText: socialPost.Post_Text.find(({ isSelected }) => isSelected)?.value,
      background: socialPost.Post_Background || socialPost.Post_Client_Brief,
      objective: socialPost.Post_Objective,
      audience: socialPost.Post_Target_Audience,
      customerInsight: socialPost.Post_Customer_Insight,
      keyMessage: socialPost.Post_Key_Message,
      rtb: socialPost.Post_RTB,
      creativeDirection: socialPost.Post_Creative_Direction,
      budget: socialPost.Post_Budget,
      totalImpact: TotalImpact,
      productPositioningImpact: ProductImpact?.TotalProductImpact,
      socialPositioningImpact: SocialPositioningImpact?.TotalSocialPositioningImpact,
      socialIssuesImpact: IssuesImpact?.TotalIssuesImpact,
      causeSupportImpact: IssuesSupportImpact?.TotalIssuesSupportImpact,
      marketingStrategyVersions: socialPost.marketingStrategyVersions,
    };
  };

  const handleRegeneration = () => {
    setStrategyRevisionDialogOpen(true);
  };

  const toggleStrategyChange = key => {
    setStrategyRevisionForm(prev => ({
      ...prev,
      selectedChanges: prev.selectedChanges.includes(key)
        ? prev.selectedChanges.filter(item => item !== key)
        : [...prev.selectedChanges, key],
    }));
  };

  const submitStrategyRegeneration = () => {
    const selectedVersionData = strategyVersions.find(version => version.versionNumber === selectedStrategyVersion);
    const selectedStrategyText = String(selectedVersionData?.text || aiText || '').trim();

    setAIStrategyGenerator({ loading: true, text: '' });
    setStrategyRevisionDialogOpen(false);
    const nextVersionNumber = strategyVersions.length + 1;
    setStrategyVersions(prev => ([
      ...prev,
      { versionNumber: nextVersionNumber, text: '', status: 'generating' },
    ]));
    setSelectedStrategyVersion(nextVersionNumber);

    const strategyData = buildStrategyData();
    const regenerationPayload = {
      ...strategyData,
      selectedStrategyText,
      currentStrategyText: aiText,
      regenerationRequest: strategyRevisionForm,
    };

    AISocialMediaStrategyRegenerator(
      regenerationPayload,
      socialPost.id,
      persona.id,
      socialPost.marketingStrategies,
      socialPost.marketingStrategyVersions,
      selectedStrategyVersion,
    )
      .then(async res => {
        setStrategyVersions(prev => prev.map(version => (
          version.versionNumber === nextVersionNumber
            ? { ...version, text: res, status: 'ready' }
            : version
        )));
        setAIStrategyGenerator({ loading: false, text: res });
        dispatch(fetchSpecificSurveyData('social', visibleAgencies));
      })
      .catch(() => {
        setAIStrategyGenerator({ loading: false, text: selectedStrategyText });
        setStrategyVersions(prev => prev.filter(version => version.versionNumber !== nextVersionNumber));
        setSelectedStrategyVersion(selectedVersionData?.versionNumber || 1);
      });
  };

  const selectedStrategyBaseText = String(
    strategyVersions.find(version => version.versionNumber === selectedStrategyVersion)?.text || aiText || ''
  ).trim();
  const selectedStrategyIsGenerating = selectedStrategyEntry?.status === 'generating';

  return (
    <Box>
      <DashDetailsWrapper
        backgroundColor="white"
        expandColor="black"
        heading1={{
          text: 'Marketing Campaign Analysis',
          color: '#19989B',
        }}>
        <div className={classes.heroSection} style={{ marginBottom: '40px' }}>
          <div className={classes.infoSection} data-page-break="true">
            <div className={classes.infoSectionHeader}>
              <span>current campaign</span>
              <h4>{socialPost.Post_Title}</h4>
            </div>
            <div className={classes.infoSectionTable}>
              <div className={classes.infoSectionTableItem}>
                <span className={classes.tableKey}>Campaign Type</span>
                <span className={classes.tableValue}>{getPostMediaType(socialPost)}</span>
              </div>
              {isSocialMedia && (
                <div className={classes.infoSectionTableItem}>
                  <span className={classes.tableKey}>Platform</span>
                  <span className={classes.tableValue}>{getSocialMediaPlatform(socialPost)}</span>
                </div>
              )}
              <div className={classes.infoSectionTableItem}>
                <span className={classes.tableKey}>Campaign Title</span>
                <span className={classes.tableValue}>{socialPost.Post_Title}</span>
              </div>
              <div className={classes.infoSectionTableItem}>
                <span className={classes.tableKey}>Campaign Date</span>
                <span className={classes.tableValue}>{moment(socialPost.Post_Date._seconds * 1000).format('LL')}</span>
              </div>
              <div className={classes.infoSectionTableItem}>
                <span className={classes.tableKey} style={{ marginBottom: '20px' }}>
                  Content Type
                </span>
                <span className={classes.tableValue} style={{ display: 'block', width: '70%' }}>
                  {getPostCategory(socialPost.Post_Category)}
                </span>
              </div>
              <div className={classes.infoSectionTableItem}>
                <span className={classes.tableKey}>Pillar 1</span>
                <span className={classes.tableValue}>{socialPost.Post_Keyword_1}</span>
              </div>

              <div className={classes.infoSectionTableItem}>
                <span className={classes.tableKey}>Pillar 2</span>
                <span className={classes.tableValue}>{socialPost.Post_Keyword_2}</span>
              </div>

              <div className={classes.infoSectionTableItem}>
                <span className={classes.tableKey}>Pillar 3</span>
                <span className={classes.tableValue}>{socialPost.Post_Keyword_3}</span>
              </div>
              <div className={classes.infoSectionTableItemPostText}>
                <p className={classes.tableKey} style={{ marginBottom: '10px' }}>
                  Campaign Pitch:
                </p>
                <p style={{ marginBottom: '10px', whiteSpace: 'pre-wrap', lineHeight: 1.55 }} className={classes.tableValue}>
                  <em>{postText}</em>
                </p>
              </div>
            </div>
          </div>
          <div className={classes.statSection}>
            <div className={classes.totalImpact}>
              <h4>Total Impact</h4>
              <p>
                Total Impact measures the potential effect marketing campaign would have on the overall alignment between the
                selected brand persona and the chosen consumer segment.
              </p>
              <Box width="fit-content" margin="0 auto">
                <div
                  id="totalImpactGaugeChart"
                  className={`report-gauge-chart report-gauge-chart-total ${classes.totalImpactGraphContainer}`}></div>
              </Box>
              <TotalImpactDetails activeScore={Math.round(Number(data?.social_post_data?.TotalImpact?.value || 0) * 100)} />
            </div>
            <div data-hide-item="true" className={classes.postEvaluation}>
              <h4>Overall Campaign Evaluation</h4>
              <p>
                This evaluation relies on the survey results. Any error in the input may result in a suboptimal
                recommendation.
              </p>
              <PublishContentCard status={data.social_post_data.TotalImpact.mainStatus} />
            </div>
          </div>
        </div>
      </DashDetailsWrapper>
      {/* YT Video */}
      <DashDetailsWrapper
        backgroundColor={isReportExport ? 'white' : '#254157'}
        expandColor={isReportExport ? 'black' : 'white'}
        heading1={{
          text: 'Marketing Campaign Strategy',
          color: isReportExport ? '#19989B' : '#fff',
        }}>
        <Box display="flex" flexDirection="column" justifyContent="flex-end" width="100%">
          {!isReportExport && (
            <div className={classes.modeSwitcher}>
              <SegmentedToggle
                value={activeMode}
                onChange={setActiveMode}
                options={isVideoEligible ? [
                  { value: 'strategy', label: 'Strategy' },
                  { value: 'moodboard', label: 'Mood Board' },
                  { value: 'video', label: 'Video' },
                ] : [
                  { value: 'strategy', label: 'Strategy' },
                  { value: 'moodboard', label: 'Mood Board' },
                ]}
              />
            </div>
          )}

          {activeMode === 'strategy' || isReportExport ? (
            <>
              {!isReportExport && strategyVersions.length > 0 && (
                <div className={classes.strategyControlsRow}>
                  <FormControl variant="outlined" size="small" className={classes.strategyVersionControl}>
                    <Select
                      className={classes.strategyVersionSelect}
                      value={selectedStrategyVersion}
                      onChange={event => setSelectedStrategyVersion(Number(event.target.value))}>
                      {strategyVersions.map(version => (
                        <MenuItem key={version.versionNumber} value={version.versionNumber}>
                          Strategy {version.versionNumber}{version.status === 'generating' ? ' (Generating...)' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <div className={classes.regenerateButton} style={{ margin: 0 }}>
                    <Button onClick={handleRegeneration} disabled={AIStrategyGenerator.loading || !selectedStrategyBaseText}>
                      Regenerate Strategy
                    </Button>
                  </div>
                </div>
              )}
              <div className={classes.AiTextWrapper} style={isReportExport ? { color: '#334155' } : undefined}>
                {aiText && !selectedStrategyIsGenerating ? (
                  <MarkdownHTMLEmbed markdown={aiText} />
                ) : selectedStrategyIsGenerating ? (
                  <>
                    <div className={classes.moodBoardStatus}>Strategy {selectedStrategyVersion} is being generated. You can view other versions from the selector.</div>
                    <div className={classes.AiLoader}>
                      <CubeLoader />
                    </div>
                  </>
                ) : (
                  <div className={classes.AiLoader}>
                    <CubeLoader />
                  </div>
                )}
                {youtubeEmbedUrl && !youtubeEmbedFailed ? (
                  <div className={classes.iFrameWrapper}>
                    <iframe
                      src={youtubeEmbedUrl}
                      title="Campaign YouTube video"
                      width="100%"
                      height="400px"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      onError={() => setYoutubeEmbedFailed(true)}
                    />
                  </div>
                ) : null}
                {youtubeEmbedUrl && youtubeEmbedFailed ? (
                  <div className={classes.iFrameWrapper}>
                    <a href={socialPost.Post_Link} target="_blank" rel="noopener noreferrer" style={{ color: '#87edef' }}>
                      Watch video on YouTube
                    </a>
                  </div>
                ) : null}
              </div>
            </>
          ) : activeMode === 'video' ? (
            <>
              {videoState.isGenerating ? (
                <div className={classes.videoRegenerateStack}>
                  <Button onClick={stopVideoGeneration} style={{ minWidth: 180 }}>
                    STOP
                  </Button>
                </div>
              ) : hasAnyVideo ? (
                <div className={classes.videoRegenerateStack}>
                  <Button
                    onClick={() => (hasDirectVideo ? openRevisionFlow('seedance') : triggerVideoGeneration('seedance'))}
                    style={{ minWidth: 180 }}>
                    {hasDirectVideo ? 'Regenerate Video' : 'Generate Video'}
                  </Button>
                </div>
              ) : null}
              <div className={classes.AiTextWrapper}>
                {videoState.isGenerating && (
                  <>
                    <div className={classes.moodBoardStatus}>Generating your direct video now. This can take a couple of minutes.</div>
                    <div className={classes.AiLoader}><CubeLoader /></div>
                  </>
                )}

                {!!videoState.seedance.error && <div className={classes.moodBoardStatus}>{videoState.seedance.error}</div>}

                {(videoState.quota?.monthlyLimit === 0 || videoState.quota?.reached) && !videoState.isGenerating ? (
                  <div className={classes.generateButtonCenter}>
                    <div className={classes.moodBoardStatus}>
                      Monthly video limit reached. Please contact sales to increase your video generation quota.
                    </div>
                    <Button onClick={openContactSalesPage}>Contact Sales</Button>
                  </div>
                ) : null}

                {!videoState.isGenerating && !hasAnyVideo && !(videoState.quota?.monthlyLimit === 0 || videoState.quota?.reached) && (
                  <div className={classes.generateButtonCenter}>
                    <Button onClick={() => triggerVideoGeneration('seedance')}>
                      Generate Video
                    </Button>
                  </div>
                )}

                {allVideos.map((video, index) => (
                  <div key={`${video.videoId || video.atlasJobId || index}_history`} style={{ marginTop: 16 }}>
                    <div className={classes.moodBoardStatus}>
                      Version {video.normalizedVersionNumber} — {providerToLabel(video?.provider)}
                      {Number.isFinite(Number(video?.baseVersionNumber)) ? ` (Based on Version ${video.baseVersionNumber})` : ''}
                    </div>
                    <video
                      controls
                      src={video.downloadURL}
                      style={{
                        width: '100%',
                        maxWidth: ['Instagram', 'TikTok'].includes(video?.platform) || video?.aspectRatio === '9:16' ? 640 : '100%',
                        borderRadius: 12,
                      }}
                    />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              {moodBoardState.data?.status === 'completed' && (
                <div className={classes.regenerateButton}>
                  <Button onClick={() => triggerMoodBoard(true)}>Regenerate Mood Board</Button>
                </div>
              )}
              <div className={classes.AiTextWrapper}>
              {!!moodBoardState.error && <div className={classes.moodBoardStatus}>{moodBoardState.error}</div>}

              {canShowMoodBoardGenerate && (
                <div className={classes.generateButtonCenter}>
                  <Button onClick={() => triggerMoodBoard(Boolean(moodBoardState.data))} disabled={moodBoardState.data?.summaries_ready === false}>
                    {moodBoardState.data?.summaries_ready === false
                      ? 'Preparing Summaries...'
                      : (moodBoardState.status === 'failed' ? 'Try Again' : 'Generate Mood Board')}
                  </Button>
                </div>
              )}

              {moodBoardState.loading && <div className={classes.AiLoader}><CubeLoader /></div>}

              {canShowMoodBoardGrid && (
                <>
                  <div className={classes.moodBoardGrid}>
                    <div className={classes.moodRow1}>
                      <div className={classes.moodCard}>
                        <h4 className={classes.moodCampaignTitle}>{socialPost.Post_Title}</h4>
                        <p>{moodBoardState.data?.summaries?.campaign_summary_100 || ''}</p>
                      </div>
                      {getMoodTile('campaign_main') ? (
                        <img className={classes.moodImg} src={getMoodTile('campaign_main')} alt="Campaign main" />
                      ) : (
                        <div className={classes.moodImgPlaceholder} style={{ aspectRatio: '3 / 2' }} />
                      )}
                    </div>
                    <div
                      className={classes.moodRow23}
                      style={{ gridTemplateColumns: isMobileViewport ? '1fr' : `repeat(${(configuredPillarIndices.length ? configuredPillarIndices : [1]).length}, 1fr)` }}>
                      {(configuredPillarIndices.length ? configuredPillarIndices : [1]).map(idx => {
                        const topImg = getMoodTile(`pillar${idx}_main`) || getMoodTile(`pillar${idx}_variant_extra`) || getMoodTile(`pillar1_variant_${idx}`);
                        const bottomImg = getMoodTile(`pillar${idx}_supporting`) || getMoodTile(`pillar1_variant_${idx + 1}`);
                        return (
                          <div className={classes.moodPillarGroupCard} key={`pillar-group-${idx}`}>
                            <div
                              ref={el => {
                                pillarTitleRefs.current[idx] = el;
                              }}
                              className={classes.moodPillarTitleWrap}
                              style={moodTitleRowHeight ? { minHeight: moodTitleRowHeight } : undefined}>
                              <h4 className={classes.moodPillarTitle}>{`Pillar ${idx}: ${moodBoardState.data?.summaries?.[`pillar${idx}_title`] || socialPost[`Post_Keyword_${idx}`] || '...'}`}</h4>
                            </div>
                            <div
                              ref={el => {
                                pillarSummaryRefs.current[idx] = el;
                              }}
                              className={classes.moodPillarSummaryWrap}
                              style={moodSummaryRowHeight ? { minHeight: moodSummaryRowHeight } : undefined}>
                              <p className={classes.moodPillarSummary}>{moodBoardState.data?.summaries?.[`pillar${idx}_summary_50`] || ''}</p>
                            </div>
                            {(configuredPillarIndices.length ? configuredPillarIndices : [1]).length === 1 ? (
                              <div className={classes.moodPillarSingleImagesRow}>
                                {topImg ? <img className={classes.moodImg} src={topImg} alt={`Pillar ${idx} top`} /> : <div className={classes.moodImgPlaceholder} style={{ aspectRatio: '1 / 1' }} />}
                                {bottomImg ? <img className={classes.moodImg} src={bottomImg} alt={`Pillar ${idx} bottom`} /> : <div className={classes.moodImgPlaceholder} style={{ aspectRatio: '1 / 1' }} />}
                              </div>
                            ) : (
                              <>
                                {topImg ? <img className={classes.moodImg} src={topImg} alt={`Pillar ${idx} top`} /> : <div className={classes.moodImgPlaceholder} style={{ aspectRatio: '1 / 1' }} />}
                                {bottomImg ? <img className={classes.moodImg} style={{ marginTop: '8px' }} src={bottomImg} alt={`Pillar ${idx} bottom`} /> : <div className={classes.moodImgPlaceholder} style={{ aspectRatio: '1 / 1', marginTop: '8px' }} />}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className={classes.moodRow23}>
                      <div className={classes.moodCard}><h4 className={classes.moodSectionTitle}>Palette</h4>{getMoodTile('palette_tile') ? <img className={classes.moodImg} src={getMoodTile('palette_tile')} alt="Palette" /> : <div className={classes.moodImgPlaceholder} style={{ aspectRatio: '2 / 3' }} />}</div>
                      <div className={classes.moodCard}><h4 className={classes.moodSectionTitle}>Textures</h4>{getMoodTile('texture_tile') ? <img className={classes.moodImg} src={getMoodTile('texture_tile')} alt="Texture" /> : <div className={classes.moodImgPlaceholder} style={{ aspectRatio: '2 / 3' }} />}</div>
                      <div className={classes.moodCard}><h4 className={classes.moodSectionTitle}>Typography</h4>{getMoodTile('typography_final') ? <img className={classes.moodImg} src={getMoodTile('typography_final')} alt="Typography" /> : <div className={classes.moodImgPlaceholder} style={{ aspectRatio: '2 / 3' }} />}</div>
                    </div>
                  </div>
                </>
              )}

              </div>
            </>
          )}
        </Box>
      </DashDetailsWrapper>
      {/* Total Marketing Campaign Impact */}
      <DashDetailsWrapper
        backgroundColor="white"
        expandColor="black"
        heading1={{
          text: 'Marketing Campaign Impact',
          color: '#19989B',
        }}>
        <div className={classes.contentImpactResponsive} data-remove-gap="true">
          <div className={classes.contentImpactItem} data-campaign-impact-width="true" data-campaign-impact-margin="true">
            <h3>Product Benefits</h3>
            <h5 style={{ height: '60px' }} data-campaign-impact-gauge-chart-font-size="true">
              {productPositionImpactData.text}
            </h5>
            <Box>
              <div
                id="productPositioningGaugeChart"
                className="report-gauge-chart"
                data-campaign-impact-gauge-chart-margin="true"></div>
            </Box>
            <Box marginTop="-100px" data-negative-top-margin="true">
              <h5>Product Positioning Impact</h5>
              <p className={classes.contentImpactParagraph}>
                Product Positioning Impact measures the effect marketing campaign would have on the product benefits match
                between selected brand persona and selected consumer segment.
              </p>
            </Box>
          </div>
          <div className={classes.contentImpactItem} data-campaign-impact-width="true" data-page-break="true">
            <h3>Social Values</h3>
            <h5 style={{ height: '60px' }} data-campaign-impact-gauge-chart-font-size="true">
              {socialPositionImpactData.text}
            </h5>
            <Box>
              <div
                id="socialPositioningGaugeChart"
                className="report-gauge-chart"
                data-campaign-impact-gauge-chart-margin="true"></div>
            </Box>
            <Box marginTop="-100px" data-negative-top-margin="true">
              <h5>Social Positioning Impact</h5>
              <p className={classes.contentImpactParagraph}>
                Social Positioning Impact measures the effect of marketing campaign on the alignment of social values between
                the selected brand persona and the chosen consumer segment.
              </p>
            </Box>
          </div>
          <div className={classes.contentImpactItem} data-campaign-impact-width="true" data-campaign-impact-margin="true">
            <h3>Social Issues</h3>
            <h5 style={{ height: '60px' }} data-campaign-impact-gauge-chart-font-size="true">
              {socialCausesImpactData.text}
            </h5>
            <Box>
              <div
                id="causesMatchImpactGaugeChart"
                className="report-gauge-chart"
                data-campaign-impact-gauge-chart-margin="true"></div>
            </Box>
            <Box marginTop="-100px" data-negative-top-margin="true">
              <h5>Issues Match Impact</h5>
              <p className={classes.contentImpactParagraph}>
                Issues Match Impact measures the effect of marketing campaign on the alignment between the social issues
                championed by the brand persona and the social issues that are important to the selected consumer segment.
              </p>
            </Box>
          </div>
          <div className={classes.contentImpactItem} data-campaign-impact-width="true">
            <h3>Causes Support</h3>
            <h5 style={{ height: '60px' }} data-campaign-impact-gauge-chart-font-size="true">
              {issuesSupportImpactData.text}
            </h5>
            <Box>
              <div
                id="issuesSupportImpactGaugeChart"
                className="report-gauge-chart"
                data-campaign-impact-gauge-chart-margin="true"></div>
            </Box>
            <Box marginTop="-100px" data-negative-top-margin="true">
              <h5>Causes Support Impact</h5>
              <p className={classes.contentImpactParagraph}>
                Causes Support Impact measures the influence of marketing campaign on the alignment between the causes
                championed by a brand's persona and the social causes supported by the targeted consumer segment.
              </p>
            </Box>
          </div>
        </div>
      </DashDetailsWrapper>
      {/* Social Positioning Analysis */}
      <DashDetailsWrapper
        backgroundColor="#254157"
        expandColor="white"
        heading1={{
          text: 'Social Positioning Analysis',
          color: '#fff',
        }}>
        <div className={classes.socialPositioningContentContainer}>
          <div className={classes.positioningHeader}>
            <div style={{ maxWidth: '500px', width: '34%' }}></div>
            <span className={classes.positioningHeaderTextElements}>Brand Image Consistency</span>
            <span className={classes.positioningHeaderTextElements}>Consumer Consistency</span>
          </div>
          {socialPositioningData.map((socialRangeChartData, index) => (
            <div
              key={index}
              style={{
                borderBottom: index == socialPositioningData.length - 1 ? 'none' : '1px solid rgba(217,217,217, 0.5)',
                padding: '20px 0px',
              }}>
              <Box className={classes.socialPositioningContent} data-remove-padding="true">
                <div className={classes.socialPositioningChart}>
                  <RangeChart
                    mainLineColor="#667A89"
                    middleLineColor="#EFEFEF"
                    leftText={{
                      text: socialRangeChartData.leftText,
                      color: 'white',
                    }}
                    rightText={{
                      text: socialRangeChartData.rightText,
                      color: 'white',
                    }}
                    valueColor="lightgray"
                    heading={socialRangeChartData.heading}
                    headingColor="white"
                    body={{
                      text: socialRangeChartData.body,
                      color: 'lightgray',
                    }}
                    value1={socialRangeChartData.personaValue}
                    value2={socialRangeChartData.consumerValue}
                    value3={socialRangeChartData.socialValue}
                    type="brand"
                  />
                </div>
                <span
                  className={classes.consistency1Text}
                  style={{
                    color: getConsistencyTextColor(socialRangeChartData.BrandImageConsistencyText),
                    marginTop: '37px',
                  }}>
                  {socialRangeChartData.BrandImageConsistencyText}
                </span>
                <span
                  className={classes.consistency2Text}
                  style={{
                    color: getConsistencyTextColor(socialRangeChartData.ConsumerConsistencyText),
                    marginTop: '37px',
                  }}>
                  {socialRangeChartData.ConsumerConsistencyText}
                </span>
              </Box>
              <p
                style={{
                  color: '#E8E8E8',
                  fontWeight: 300,
                  width: '35%',
                  fontSize: '15px',
                }}
                className={classes.chartBodyText}>
                {socialRangeChartData.bodyText}
              </p>

              <div
                className={classes.consistencyTextSm}
                style={{
                  color: getConsistencyTextColor(socialRangeChartData.BrandImageConsistencyText),
                }}>
                <span className={classes.consistencyName}>Brand Image Consistency:</span>{' '}
                {socialRangeChartData.BrandImageConsistencyText}
              </div>
              <div
                className={classes.consistencyTextSm}
                style={{
                  color: getConsistencyTextColor(socialRangeChartData.ConsumerConsistencyText),
                }}>
                <span className={classes.consistencyName}>Consumer Consistency:</span>{' '}
                {socialRangeChartData.ConsumerConsistencyText}
              </div>
            </div>
          ))}
          <div className={classes.positioiningLegends} data-report-legend="true">
            <div className={classes.positioningLegend}>
              <div style={{ background: '#D9B14A' }}></div>
              {truncateLegendText(socialPost.Post_Title, viewportWidth)}
            </div>
            <div className={classes.positioningLegend}>
              <div style={{ background: '#CD225C' }}></div>SELECTED SAMPLE
            </div>
            <div className={classes.positioningLegend}>
              <div style={{ background: '#23A9A9' }}></div>
              {truncateLegendText(persona.Name, viewportWidth)}
            </div>
          </div>
        </div>
      </DashDetailsWrapper>
      {/* Product Benefits Analysis */}
      <DashDetailsWrapper
        backgroundColor="white"
        expandColor="black"
        heading1={{
          text: 'Product Benefits Analysis',
          color: '#19989B',
        }}>
        <div>
          <p className={classes.subtitle} data-report-subtitle="true">
            Positions consumers and brands take on regarding different product features and attributes.
          </p>
          <Box className={classes.productBenefitsResponsive} data-report-chart-grid="true">
            {productBenefitsData.map((item, idx) => {
              return (
                <Box className={classes.rangeChartWrapper} key={idx} data-report-chart-item="true">
                  <RangeChart
                    mainLineColor="#E5E6E6"
                    middleLineColor="#D9D9D9"
                    leftText={{
                      text: item.leftText,
                      color: '#6C757D',
                    }}
                    rightText={{
                      text: item.rightText,
                      color: '#6C757D',
                    }}
                    valueColor="gray"
                    body={{
                      text: item.body,
                      color: '#6C757D',
                    }}
                    value1={item.personaValue}
                    value2={item.consumerValue}
                    value3={item.socialValue}
                    type="brand"
                  />
                  <span className={classes.consistencyBenefitsText}>
                    Consumer Consistency -{' '}
                    <span
                      style={{
                        color: getProductBenefitsSpanColor(item.ConsumerConsistencyText),
                      }}>
                      {item.ConsumerConsistencyText}
                    </span>
                  </span>
                  <span className={classes.consistencyBenefitsText}>
                    Brand Image Consistency -{' '}
                    <span
                      style={{
                        color: getProductBenefitsSpanColor(item.BrandImageConsistencyText),
                      }}>
                      {item.BrandImageConsistencyText}
                    </span>
                  </span>
                </Box>
              );
            })}
          </Box>
          <div className={classes.positioiningLegends} data-report-legend="true">
            <div className={classes.productBenefitsLegend}>
              <div style={{ background: '#D9B14A' }}></div>
              {truncateLegendText(socialPost.Post_Title, viewportWidth)}
            </div>
            <div className={classes.productBenefitsLegend}>
              <div style={{ background: '#CD225C' }}></div>SELECTED SAMPLE
            </div>
            <div className={classes.productBenefitsLegend}>
              <div style={{ background: '#23A9A9' }}></div>
              {truncateLegendText(persona.Name, viewportWidth)}
            </div>
          </div>
        </div>
      </DashDetailsWrapper>

      {/* Social Issues Analysis - Consumer Consistency */}
      <DashDetailsWrapper
        backgroundColor="#254157"
        expandColor="white"
        heading1={{
          text: 'Social Issues Analysis - Consumer Consistency',
          color: '#fff',
        }}>
        <Box className={classes.consumerConsistencyContent} data-report-social-issues="true">
          {issuesImpact.map(item => {
            return (
              <ConsumerConsistencyCard
                key={item.id}
                id={item.id}
                subject={item.subject}
                text={item.text}
                value={item.value}
                textColor={item.textColor}
              />
            );
          })}
          </Box>
        </DashDetailsWrapper>

      {/* Social Causes Support Analysis */}
      <DashDetailsWrapper
        backgroundColor="white"
        expandColor="black"
        heading1={{
          text: 'Social Causes Support Analysis',
          color: '#19989B',
        }}>
        <div>
          <p className={classes.subtitle} data-report-subtitle="true">
            Explores the alignment between consumer support and brand advocacy for 18 social and political issues, including
            topics such as abortion rights, free healthcare, gun restrictions, police reform, same-sex marriage, and other
            key issues shaping public discourse and corporate responsibility.
          </p>
          <Box className={classes.productBenefitsResponsive} data-report-chart-grid="true">
            {issuesSupportData.map((item, idx) => {
              return (
                <Box className={classes.rangeChartWrapper} key={idx} data-report-chart-item="true">
                  <RangeChart
                    mainLineColor="#E5E6E6"
                    middleLineColor="#D9D9D9"
                    heading={item.heading}
                    headingColor="#515151"
                    leftText={{
                      text: item.leftText,
                      color: '#6C757D',
                    }}
                    rightText={{
                      text: item.rightText,
                      color: '#6C757D',
                    }}
                    valueColor="gray"
                    body={{
                      text: item.body,
                      color: '#6C757D',
                    }}
                    value1={item.personaValue}
                    value2={item.consumerValue}
                    value3={item.socialValue}
                    type="brand"
                  />
                  <span className={classes.consistencyBenefitsText}>
                    Consumer Consistency -{' '}
                    <span
                      style={{
                        color: getProductBenefitsSpanColor(item.ConsumerConsistencyText),
                      }}>
                      {item.ConsumerConsistencyText}
                    </span>
                  </span>
                  <span className={classes.consistencyBenefitsText}>
                    Brand Image Consistency -{' '}
                    <span
                      style={{
                        color: getProductBenefitsSpanColor(item.BrandImageConsistencyText),
                      }}>
                      {item.BrandImageConsistencyText}
                    </span>
                  </span>
                </Box>
              );
            })}
          </Box>
          <div
            className={classes.positioiningLegends}
            style={{ marginBottom: '20px' }}
            data-report-legend="true"
            data-keep-with-chart="true">
            <div className={classes.productBenefitsLegend}>
              <div style={{ background: '#D9B14A' }}></div>
              {truncateLegendText(socialPost.Post_Title, viewportWidth)}
            </div>
            <div className={classes.productBenefitsLegend}>
              <div style={{ background: '#CD225C' }}></div>SELECTED SAMPLE
            </div>
            <div className={classes.productBenefitsLegend}>
              <div style={{ background: '#23A9A9' }}></div>
              {truncateLegendText(persona.Name, viewportWidth)}
            </div>
          </div>
          <Watermark />
        </div>
        </DashDetailsWrapper>
      <Dialog
        open={strategyRevisionDialogOpen}
        onClose={() => setStrategyRevisionDialogOpen(false)}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.revisionDialogPaper }}>
        <DialogTitle>
          Regenerate Strategy
          <IconButton aria-label="Close strategy regeneration dialog" className={classes.revisionDialogCloseBtn} onClick={() => setStrategyRevisionDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography className={classes.revisionSubtitle}>What should be changed?</Typography>
          <div className={classes.revisionSection}>
            <FormGroup>
              {STRATEGY_REGEN_CHANGE_OPTIONS.map(option => (
                <FormControlLabel
                  key={option.key}
                  control={<Checkbox checked={strategyRevisionForm.selectedChanges.includes(option.key)} onChange={() => toggleStrategyChange(option.key)} color="primary" />}
                  label={option.label}
                />
              ))}
            </FormGroup>
          </div>

          {strategyRevisionForm.selectedChanges.includes('objective') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>What should the updated objective be?</Typography>
              <TextField fullWidth multiline rows={2} variant="outlined" value={strategyRevisionForm.objectiveUpdate} onChange={e => setStrategyRevisionForm(prev => ({ ...prev, objectiveUpdate: e.target.value }))} />
            </div>
          )}
          {strategyRevisionForm.selectedChanges.includes('target_audience') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>How should the target audience change?</Typography>
              <TextField fullWidth multiline rows={2} variant="outlined" value={strategyRevisionForm.audienceUpdate} onChange={e => setStrategyRevisionForm(prev => ({ ...prev, audienceUpdate: e.target.value }))} />
            </div>
          )}
          {strategyRevisionForm.selectedChanges.includes('customer_insight') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>What is the revised or refined customer insight?</Typography>
              <TextField fullWidth multiline rows={2} variant="outlined" value={strategyRevisionForm.customerInsightUpdate} onChange={e => setStrategyRevisionForm(prev => ({ ...prev, customerInsightUpdate: e.target.value }))} />
            </div>
          )}
          {strategyRevisionForm.selectedChanges.includes('messaging_guardrails') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>What should be avoided or emphasized in messaging?</Typography>
              <TextField fullWidth multiline rows={2} variant="outlined" value={strategyRevisionForm.messagingGuardrailsUpdate} onChange={e => setStrategyRevisionForm(prev => ({ ...prev, messagingGuardrailsUpdate: e.target.value }))} />
            </div>
          )}
          {strategyRevisionForm.selectedChanges.includes('campaign_approach') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>What should change in the strategy or execution approach?</Typography>
              <TextField fullWidth multiline rows={2} variant="outlined" value={strategyRevisionForm.campaignApproachUpdate} onChange={e => setStrategyRevisionForm(prev => ({ ...prev, campaignApproachUpdate: e.target.value }))} />
            </div>
          )}
          {strategyRevisionForm.selectedChanges.includes('revised_copy') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>What direction should the updated copy take?</Typography>
              <TextField fullWidth multiline rows={2} variant="outlined" value={strategyRevisionForm.revisedCopyDirection} onChange={e => setStrategyRevisionForm(prev => ({ ...prev, revisedCopyDirection: e.target.value }))} />
            </div>
          )}
          {strategyRevisionForm.selectedChanges.includes('budget_allocation') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>Budget & Allocation</Typography>
              <Box display="flex" gridGap={8}>
                <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                  <InputLabel id="strategy-budget-currency">Currency</InputLabel>
                  <Select
                    labelId="strategy-budget-currency"
                    value={strategyRevisionForm.budgetCurrency}
                    onChange={e => setStrategyRevisionForm(prev => ({ ...prev, budgetCurrency: e.target.value }))}
                    label="Currency">
                    {STRATEGY_BUDGET_CURRENCIES.map(currency => (
                      <MenuItem key={currency.value} value={currency.value}>
                        {currency.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField variant="outlined" size="small" label="Amount" value={strategyRevisionForm.budgetAmount} onChange={e => setStrategyRevisionForm(prev => ({ ...prev, budgetAmount: e.target.value }))} />
              </Box>
              <TextField style={{ marginTop: 8 }} fullWidth multiline rows={2} variant="outlined" label="How should the budget be distributed?" value={strategyRevisionForm.budgetDistribution} onChange={e => setStrategyRevisionForm(prev => ({ ...prev, budgetDistribution: e.target.value }))} />
            </div>
          )}
          {strategyRevisionForm.selectedChanges.includes('timeline_execution') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>What should the updated timeline and rollout plan be?</Typography>
              <TextField fullWidth multiline rows={2} variant="outlined" value={strategyRevisionForm.timelineUpdate} onChange={e => setStrategyRevisionForm(prev => ({ ...prev, timelineUpdate: e.target.value }))} />
            </div>
          )}

          <div className={classes.revisionSection}>
            <Typography className={classes.revisionSectionTitle}>What else should be added, removed, or adjusted in the strategy?</Typography>
            <TextField fullWidth multiline rows={3} variant="outlined" value={strategyRevisionForm.additionalNotes} onChange={e => setStrategyRevisionForm(prev => ({ ...prev, additionalNotes: e.target.value }))} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStrategyRevisionDialogOpen(false)}>Cancel</Button>
          <Button onClick={submitStrategyRegeneration} color="primary" variant="contained" disabled={AIStrategyGenerator.loading}>
            Regenerate Strategy
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={revisionState.open}
        onClose={closeRevisionFlow}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.revisionDialogPaper }}>
        <DialogTitle>
          Revise Video
          <IconButton aria-label="Close revision dialog" className={classes.revisionDialogCloseBtn} onClick={closeRevisionFlow}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography className={classes.revisionSubtitle}>
            Revision mode: Direct
          </Typography>
          {revisionState.baseVideoLabel ? (
            <Typography className={classes.revisionSubtitle}>
              Revising from: {revisionState.baseVideoLabel}
            </Typography>
          ) : null}
          <Typography className={classes.revisionSubtitle}>Tell us what should change before we generate a new version.</Typography>
          {revisionState.step === 'select_base' && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>Which video do you want to revise?</Typography>
              <RadioGroup
                value={revisionState.baseVideoId}
                onChange={event => {
                  const selected = revisionCandidates.find(video => String(video.videoId || video.atlasJobId || '') === event.target.value);
                  const draftKey = `videoRevisionDraft:${socialPost.id}:${event.target.value}:${revisionState.regenerationMode}`;
                  let draft = {};
                  try {
                    draft = JSON.parse(localStorage.getItem(draftKey) || '{}');
                  } catch (err) {
                    draft = {};
                  }
                  setRevisionState(prev => ({
                    ...prev,
                    baseVideoId: event.target.value,
                    baseVersionNumber: selected?.normalizedVersionNumber || null,
                    baseVideoLabel: selected ? `Version ${selected.normalizedVersionNumber} — ${providerToLabel(selected.provider)}` : '',
                    step: 'questions',
                    selectedChanges: draft.selectedChanges || prev.selectedChanges,
                    storyMessageChange: draft.storyMessageChange || prev.storyMessageChange,
                    visualStyle: draft.visualStyle || prev.visualStyle,
                    visualStyleNotes: draft.visualStyleNotes || prev.visualStyleNotes,
                    toneEmotion: draft.toneEmotion || prev.toneEmotion,
                    toneNotes: draft.toneNotes || prev.toneNotes,
                    pacingStructure: draft.pacingStructure || prev.pacingStructure,
                    sceneDensity: draft.sceneDensity || prev.sceneDensity,
                    structureNotes: draft.structureNotes || prev.structureNotes,
                    openingType: draft.openingType || prev.openingType,
                    openingNotes: draft.openingNotes || prev.openingNotes,
                    endingGoals: draft.endingGoals || prev.endingGoals,
                    endingNotes: draft.endingNotes || prev.endingNotes,
                    submitError: '',
                  }));
                }}>
                {revisionCandidates.map(video => {
                  const videoId = String(video.videoId || video.atlasJobId || '');
                  return (
                    <FormControlLabel
                      key={videoId}
                      value={videoId}
                      control={<Radio color="primary" />}
                      label={`Version ${video.normalizedVersionNumber} — ${providerToLabel(video.provider)}`}
                    />
                  );
                })}
              </RadioGroup>
            </div>
          )}
          {revisionState.step !== 'questions' ? null : (
          <>
          <div className={classes.revisionSection}>
            <Typography className={classes.revisionSectionTitle}>What do you want to change?</Typography>
            <FormGroup>
              {REVISION_CHANGE_OPTIONS.map(option => (
                <FormControlLabel
                  key={option.key}
                  control={<Checkbox checked={revisionState.selectedChanges.includes(option.key)} onChange={() => toggleRevisionChange(option.key)} color="primary" />}
                  label={option.label}
                />
              ))}
            </FormGroup>
          </div>

          {revisionState.selectedChanges.includes('story_message') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>Describe how the story or message should change.</Typography>
              <TextField multiline rows={3} fullWidth variant="outlined" placeholder="Examples: stronger product focus, simpler story, different setting..." value={revisionState.storyMessageChange} onChange={e => setRevisionState(prev => ({ ...prev, storyMessageChange: e.target.value }))} />
            </div>
          )}
          {revisionState.selectedChanges.includes('visual_style') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>What visual direction do you want?</Typography>
              <RadioGroup value={revisionState.visualStyle} onChange={e => setRevisionState(prev => ({ ...prev, visualStyle: e.target.value }))}>
                <FormControlLabel value="high_end_commercial" control={<Radio color="primary" />} label="High-end commercial (clean, polished, brand-forward)" />
                <FormControlLabel value="realistic_documentary" control={<Radio color="primary" />} label="Realistic / documentary (natural, unstaged)" />
                <FormControlLabel value="social_ugc" control={<Radio color="primary" />} label="Social / UGC (phone-shot, raw, native content feel)" />
                <FormControlLabel value="animated_stylized" control={<Radio color="primary" />} label="Animated / stylized (cartoon, 3D, motion design)" />
                <FormControlLabel value="minimal" control={<Radio color="primary" />} label="Minimal (very simple, few elements, restrained visuals)" />
              </RadioGroup>
              <TextField style={{ marginTop: 8 }} multiline rows={2} fullWidth variant="outlined" label="Add specific visual references (colors, locations, camera, style)" value={revisionState.visualStyleNotes} onChange={e => setRevisionState(prev => ({ ...prev, visualStyleNotes: e.target.value }))} />
            </div>
          )}
          {revisionState.selectedChanges.includes('tone_emotion') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>What should the tone be?</Typography>
              <RadioGroup value={revisionState.toneEmotion} onChange={e => setRevisionState(prev => ({ ...prev, toneEmotion: e.target.value }))}>
                <FormControlLabel value="fun_playful" control={<Radio color="primary" />} label="Fun / playful" />
                <FormControlLabel value="inspirational" control={<Radio color="primary" />} label="Inspirational" />
                <FormControlLabel value="emotional" control={<Radio color="primary" />} label="Emotional" />
                <FormControlLabel value="bold_provocative" control={<Radio color="primary" />} label="Bold / provocative" />
                <FormControlLabel value="calm_neutral" control={<Radio color="primary" />} label="Calm / neutral" />
              </RadioGroup>
              <TextField style={{ marginTop: 8 }} multiline rows={2} fullWidth variant="outlined" label="What should feel different from the current version?" value={revisionState.toneNotes} onChange={e => setRevisionState(prev => ({ ...prev, toneNotes: e.target.value }))} />
            </div>
          )}
          {revisionState.selectedChanges.includes('pacing_structure') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>How should pacing change?</Typography>
              <RadioGroup value={revisionState.pacingStructure} onChange={e => setRevisionState(prev => ({ ...prev, pacingStructure: e.target.value }))}>
                <FormControlLabel value="faster" control={<Radio color="primary" />} label="Faster (more cuts, higher energy)" />
                <FormControlLabel value="balanced" control={<Radio color="primary" />} label="Balanced" />
                <FormControlLabel value="slower" control={<Radio color="primary" />} label="Slower (longer shots, smoother pacing)" />
              </RadioGroup>
              <RadioGroup value={revisionState.sceneDensity} onChange={e => setRevisionState(prev => ({ ...prev, sceneDensity: e.target.value }))}>
                <FormControlLabel value="fewer_simpler" control={<Radio color="primary" />} label="Fewer, simpler" />
                <FormControlLabel value="keep_similar" control={<Radio color="primary" />} label="Keep similar" />
                <FormControlLabel value="more_dynamic" control={<Radio color="primary" />} label="More dynamic" />
              </RadioGroup>
              <TextField style={{ marginTop: 8 }} multiline rows={2} fullWidth variant="outlined" label="Describe any structural changes" value={revisionState.structureNotes} onChange={e => setRevisionState(prev => ({ ...prev, structureNotes: e.target.value }))} />
            </div>
          )}
          {revisionState.selectedChanges.includes('opening') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>How should the video start?</Typography>
              <RadioGroup value={revisionState.openingType} onChange={e => setRevisionState(prev => ({ ...prev, openingType: e.target.value }))}>
                <FormControlLabel value="strong_visual_hook" control={<Radio color="primary" />} label="Strong visual hook" />
                <FormControlLabel value="direct_message_statement" control={<Radio color="primary" />} label="Direct message / statement" />
                <FormControlLabel value="character_moment" control={<Radio color="primary" />} label="Character moment" />
                <FormControlLabel value="product_first" control={<Radio color="primary" />} label="Product-first" />
              </RadioGroup>
              <TextField style={{ marginTop: 8 }} multiline rows={2} fullWidth variant="outlined" label="Describe the first 3 seconds" value={revisionState.openingNotes} onChange={e => setRevisionState(prev => ({ ...prev, openingNotes: e.target.value }))} />
            </div>
          )}
          {revisionState.selectedChanges.includes('ending') && (
            <div className={classes.revisionSection}>
              <Typography className={classes.revisionSectionTitle}>What should improve in the ending?</Typography>
              <FormGroup>
                {[
                  ['clearer_message', 'Clearer message'],
                  ['stronger_emotion', 'Stronger emotion'],
                  ['more_memorable_visual', 'More memorable visual'],
                  ['stronger_product_focus', 'Stronger product focus'],
                  ['clear_call_to_action', 'Clear call to action'],
                ].map(([key, label]) => (
                  <FormControlLabel key={key} control={<Checkbox checked={revisionState.endingGoals.includes(key)} onChange={() => setRevisionState(prev => ({ ...prev, endingGoals: prev.endingGoals.includes(key) ? prev.endingGoals.filter(item => item !== key) : [...prev.endingGoals, key] }))} color="primary" />} label={label} />
                ))}
              </FormGroup>
              <TextField style={{ marginTop: 8 }} multiline rows={2} fullWidth variant="outlined" label="Describe the desired ending" value={revisionState.endingNotes} onChange={e => setRevisionState(prev => ({ ...prev, endingNotes: e.target.value }))} />
            </div>
          )}
          {!!revisionState.submitError && <Typography className={classes.revisionError}>{revisionState.submitError}</Typography>}
          {!!revisionState.scriptRevisionError && <Typography className={classes.revisionError}>{revisionState.scriptRevisionError}</Typography>}
          {!!revisionState.videoGenerationError && <Typography className={classes.revisionError}>{revisionState.videoGenerationError}</Typography>}
          {revisionState.isRevisingScript && <Typography className={classes.revisionSubtitle}>Revising script…</Typography>}
          {revisionState.isGeneratingVideo && <Typography className={classes.revisionSubtitle}>Script updated. Starting video generation…</Typography>}
          </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRevisionFlow} disabled={revisionState.isSubmitting}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={revisionState.step === 'select_base'
              ? () => setRevisionState(prev => ({ ...prev, step: prev.baseVideoId ? 'questions' : 'select_base', submitError: prev.baseVideoId ? '' : 'Please select a version to revise.' }))
              : submitRevision}
            disabled={revisionState.isSubmitting}>
            {revisionState.step === 'select_base'
              ? 'Continue'
              : (revisionState.isSubmitting ? 'Processing…' : 'Generate Revised Video')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default memo(ChartCalculations);
